import React from 'react';
import { Platform, StyleSheet, Text, View } from 'react-native';
import Svg, { Circle } from 'react-native-svg';
import { FontFamily } from '../../../../../constants/fonts';
import type { WesApiDistribution } from '../wesPalletStagingApi';
import { formatApiCount, formatCompactCount } from '../wesPalletStagingMappers';
import type { WesStagingScope } from '../wesPalletStagingStaticRows';
import WesSectionState, { type WesSectionStatus } from './WesSectionState';
import { WES_CARD, WES_CARD_SUBTITLE, WES_CARD_TITLE } from './wesCardStyle';

interface Category {
  key: 'not' | 'audit' | 'other';
  label: string;
  color: string;
  count: (d: WesApiDistribution) => number | null;
  pct: (d: WesApiDistribution) => number | null;
  // Staged categories excluded by the Staging Scope are de-emphasized.
  excludedBy?: WesStagingScope;
}

const CATEGORIES: Category[] = [
  { key: 'not', label: 'Not Staged', color: '#1789EE', count: (d) => d.notStaged, pct: (d) => d.notStagedPct },
  { key: 'audit', label: 'Staged (Audit-Related)', color: '#F5A623', count: (d) => d.stagedAuditRelated, pct: (d) => d.stagedAuditRelatedPct, excludedBy: 'other' },
  { key: 'other', label: 'Staged (Other)', color: '#EB6B2D', count: (d) => d.stagedOther, pct: (d) => d.stagedOtherPct, excludedBy: 'audit' },
];

// Doughnut with a 62 percent cutout, as in the mockup.
const SIZE = 280;
const OUTER = SIZE / 2;
const STROKE = OUTER * 0.38;
const RADIUS = OUTER - STROKE / 2;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;
const GAP = 2;
const EXCLUDED_OPACITY = 0.15;

const formatCategoryPercent = (pct: number | null): string => (pct === null ? '-' : `${pct.toFixed(2)}%`);

interface WesDistributionCardProps {
  data: WesApiDistribution | null;
  scope: WesStagingScope;
  status?: WesSectionStatus;
}

// Counts are fixed regardless of the Staging Scope; the scope only dims excluded categories. A category with no count (audit split unavailable) is not drawn.
const WesDistributionCard: React.FC<WesDistributionCardProps> = ({ data, scope, status }) => {
  const [activeTooltip, setActiveTooltip] = React.useState<string | null>(null);
  const total = data?.total ?? 0;
  const zeroTotal = total === 0;
  let offset = 0;
  const tooltipCategory = CATEGORIES.find((category) => category.key === activeTooltip);
  const tooltipCount = data && tooltipCategory ? tooltipCategory.count(data) : null;
  const tooltipText = data && tooltipCategory && tooltipCount !== null
    ? `${tooltipCategory.label}: ${formatApiCount(tooltipCount)} (${formatCategoryPercent(tooltipCategory.pct(data))} of total pallets)${tooltipCategory.excludedBy === scope ? '\nExcluded by Staging Scope filter' : ''}`
    : null;

  return (
    <View style={styles.card} testID="wes-distribution">
      <Text style={styles.title}>Pallet Staging Distribution</Text>
      <Text style={styles.subtitle}>Pallets by staging category</Text>
      <WesSectionState status={status} skeleton="chart" testID="wes-distribution">
        {data ? (
          <View style={styles.body}>
            <View style={styles.donut}>
              <Svg width={SIZE} height={SIZE} viewBox={`0 0 ${SIZE} ${SIZE}`}>
                <Circle testID="wes-distribution-ring" cx={OUTER} cy={OUTER} r={RADIUS} stroke="#EBEBEB" strokeWidth={STROKE} fill="none" />
                {CATEGORIES.map((category) => {
                  const count = category.count(data);
                  if (count === null || total === 0) return null;
                  const length = (count / total) * CIRCUMFERENCE;
                  const excluded = category.excludedBy === scope;
                  const tooltip = `${category.label}: ${formatApiCount(count)} (${formatCategoryPercent(category.pct(data))} of total pallets)${excluded ? '\nExcluded by Staging Scope filter' : ''}`;
                  const segment = (
                    <Circle
                      key={category.key}
                      testID={`wes-dist-slice-${category.key}`}
                      cx={OUTER}
                      cy={OUTER}
                      r={RADIUS}
                      stroke={category.color}
                      strokeOpacity={excluded ? EXCLUDED_OPACITY : 1}
                      strokeWidth={STROKE}
                      strokeDasharray={`${Math.max(0, length - GAP)} ${CIRCUMFERENCE - Math.max(0, length - GAP)}`}
                      strokeDashoffset={-offset}
                      fill="none"
                      rotation={-90}
                      origin={`${OUTER}, ${OUTER}`}
                      accessibilityLabel={tooltip}
                      onPress={() => setActiveTooltip((current) => (current === category.key ? null : category.key))}
                      {...(Platform.OS === 'web'
                        ? ({
                            onMouseEnter: () => setActiveTooltip(category.key),
                            onMouseLeave: () => setActiveTooltip((current: string | null) => (current === category.key ? null : current)),
                          } as any)
                        : {})}
                    />
                  );
                  offset += length;
                  return segment;
                })}
              </Svg>
              <View style={styles.center} pointerEvents="none">
                <Text style={styles.centerValue} testID="wes-distribution-total">{formatCompactCount(total)}</Text>
                <Text style={styles.centerLabel}>Total Pallets</Text>
              </View>
              {activeTooltip ? (
                <View style={styles.sliceTooltip} pointerEvents="none" testID="wes-distribution-tooltip">
                  <Text style={styles.sliceTooltipText}>{tooltipText}</Text>
                </View>
              ) : null}
            </View>

            <View style={styles.list}>
              {CATEGORIES.map((category) => {
                const excluded = category.excludedBy === scope;
                const count = category.count(data);
                const pct = category.pct(data);
                const displayCount = zeroTotal ? 0 : count;
                const displayPct = zeroTotal ? 0 : pct;
                return (
                  <View key={category.key} style={styles.row} testID={`wes-dist-row-${category.key}`}>
                    <View style={styles.rowTop}>
                      <View style={styles.dot}>
                        <View testID={`wes-dist-swatch-${category.key}`} style={[StyleSheet.absoluteFillObject, { backgroundColor: category.color, opacity: excluded ? EXCLUDED_OPACITY : 1 }]} />
                      </View>
                      <View style={[styles.rowText, excluded && styles.rowExcluded]}>
                        <Text style={styles.rowLabel}>{category.label}</Text>
                        {excluded ? <Text style={styles.excluded}>Excluded by Staging Scope filter</Text> : null}
                      </View>
                      <Text style={[styles.rowCount, excluded && styles.rowExcluded]} testID={`wes-dist-count-${category.key}`}>{formatApiCount(displayCount)}</Text>
                      <Text style={[styles.rowPct, excluded && styles.rowExcluded]}>{zeroTotal ? '0.00%' : formatCategoryPercent(displayPct)}</Text>
                    </View>
                    <View style={styles.bar}>
                      <View testID={`wes-dist-bar-${category.key}`} style={[styles.barFill, { backgroundColor: category.color, width: `${Math.min(100, displayPct ?? 0)}%`, opacity: excluded ? EXCLUDED_OPACITY : 1 }]} />
                    </View>
                  </View>
                );
              })}
            </View>
          </View>
        ) : null}
      </WesSectionState>
    </View>
  );
};

const styles = StyleSheet.create({
  card: { ...WES_CARD, flex: 1, minWidth: 0 },
  title: WES_CARD_TITLE,
  subtitle: WES_CARD_SUBTITLE,
  body: { flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'center', columnGap: 24, rowGap: 16 },
  donut: { width: SIZE, height: SIZE, alignItems: 'center', justifyContent: 'center' },
  sliceTooltip: { position: 'absolute', top: 8, left: 8, maxWidth: 264, paddingHorizontal: 11, paddingVertical: 7, borderRadius: 6, backgroundColor: '#2D2D2D', zIndex: 20 },
  sliceTooltipText: { fontSize: 12, lineHeight: 18, fontWeight: '600', fontFamily: FontFamily.medium, color: '#FFFFFF' },
  center: { position: 'absolute', alignItems: 'center' },
  centerValue: { fontSize: 26, fontFamily: FontFamily.bold, color: '#2D2D2D' },
  centerLabel: { fontSize: 11, fontFamily: FontFamily.bold, color: '#838383', textTransform: 'uppercase' },
  list: { width: 250, maxWidth: '100%', rowGap: 10 },
  row: { borderWidth: 1, borderColor: '#E6E6E6', borderRadius: 8, paddingVertical: 9, paddingHorizontal: 12, rowGap: 8 },
  rowTop: { flexDirection: 'row', alignItems: 'center', columnGap: 12 },
  rowExcluded: { opacity: 0.4 },
  dot: { width: 14, height: 14, borderRadius: 3, backgroundColor: '#EBEBEB', overflow: 'hidden' },
  rowText: { flex: 1 },
  rowLabel: { fontSize: 13, fontFamily: FontFamily.bold, color: '#2D2D2D' },
  excluded: { fontSize: 11, fontFamily: FontFamily.regular, color: '#838383' },
  rowCount: { fontSize: 13, fontFamily: FontFamily.regular, color: '#616161' },
  rowPct: { fontSize: 16, fontFamily: FontFamily.bold, color: '#2D2D2D', minWidth: 60, textAlign: 'right' },
  bar: { height: 6, borderRadius: 3, backgroundColor: '#EBEBEB', overflow: 'hidden' },
  barFill: { height: 6 },
});

export default WesDistributionCard;
